import { DownloadOutlined, FileTextOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, message, Modal, Select, Space, Tag } from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '../../services/contactsApi';

// Firebase Storage URL for branding banner
const FIREBASE_BANNER_URL = 'https://firebasestorage.googleapis.com/v0/b/ci-ai-agent.firebasestorage.app/o/branding%2FCI_Banner.png?alt=media&token=c8768f5f-525f-41df-a997-9766d9343001';

const { TextArea } = Input;
const { Option } = Select;

const normalizeSkillList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((v) => String(v || '').trim())
      .filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const SendCandidateEmailModal = ({ candidate, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formatting, setFormatting] = useState(false);
  const [selectedCvPdfB64, setSelectedCvPdfB64] = useState(null);
  const [jdFormatting, setJdFormatting] = useState(false);
  const [selectedJdPdfB64, setSelectedJdPdfB64] = useState(null);
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [anonymizeName, setAnonymizeName] = useState(false);

  const safeSelectFilter = (input, option) => {
    const searchInput = String(input || '').toLowerCase();
    const optionLabel = String(option?.label ?? option?.children ?? '').toLowerCase();
    return optionLabel.includes(searchInput);
  };

  const normalizeJobs = (payload) => {
    const rawJobs = Array.isArray(payload?.job_descriptions)
      ? payload.job_descriptions
      : (Array.isArray(payload?.jobs) ? payload.jobs : []);

    return rawJobs.map((job) => ({
      id: job?.id || '',
      title: job?.title || 'Untitled Role',
      company: job?.company || 'Unknown company',
      description: job?.description || '',
      location: job?.location || '',
      status: job?.status || 'active'
    })).filter((job) => !!job.id);
  };

  // Fetch available jobs for the user
  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/job_descriptions?limit=100', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        const normalizedJobs = normalizeJobs(response.data);
        setJobs(normalizedJobs);
        console.log('[SendCandidateEmailModal] fetched jobs:', {
          count: normalizedJobs.length,
          preview: normalizedJobs.slice(0, 3).map((j) => ({ id: j.id, title: j.title }))
        });
        if (normalizedJobs.length === 0) {
          message.info('No jobs found for your account yet. Please create or upload a job first.');
        }
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      message.error('Failed to fetch jobs. Please try again.');
      setJobs([]);
    }
  };

  // Fetch email templates for candidate outreach
  const fetchEmailTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get('/api/email-templates?type=candidate', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        const templates = response.data.templates || [];
        setEmailTemplates(templates);
        if (templates.length === 0) {
          console.log('No email templates found. Create templates via API or Settings page.');
        }
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      // Don't show error message as templates are optional
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle job selection - fetch full job data for CV formatting
  const handleJobSelect = async (jobId) => {
    setSelectedJob(jobId);
    setSelectedCvPdfB64(null); // Clear previous CV when job changes
    setSelectedJdPdfB64(null); // Clear previous JD PDF when job changes
    setSelectedJobData(null); // Clear previous job data to prevent mismatch
    setAnonymizeName(false); // Reset anonymization when job changes (let user re-choose)

    // Fetch full job data for CV formatting
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/get_job_description/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data) {
        setSelectedJobData(response.data);
      }
    } catch (error) {
      console.error('Error fetching job data:', error);
      // Continue without job data, CV formatting will work with basic info
    }
  };

  // Generate branded CV
  const handlePreviewBrandedCV = async () => {
    if (!candidate || !selectedJob) {
      message.warning('Please select a job first');
      return;
    }

    try {
      setFormatting(true);

      // Always try to hydrate the latest full candidate payload so CV formatting
      // is based on full resume text (not the lightweight table row).
      let hydratedCandidate = { ...candidate };
      const token = localStorage.getItem('token');
      if (candidate?.id && token) {
        try {
          const hydrateResp = await axios.get(`/api/candidates/${encodeURIComponent(candidate.id)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (hydrateResp?.data?.success && hydrateResp.data.candidate) {
            hydratedCandidate = { ...hydratedCandidate, ...hydrateResp.data.candidate };
          }
        } catch (hydrateError) {
          console.warn('Could not hydrate full candidate before CV format, using current candidate payload:', hydrateError);
        }
      }

      // Prefer full resume text from hydrated payload.
      let fullText = (
        hydratedCandidate.fullText ||
        hydratedCandidate.full_text ||
        hydratedCandidate.text ||
        ''
      );

      // If fullText is not available, we can still generate CV with available data
      // The backend can handle missing fullText by constructing from other fields
      if (!fullText && hydratedCandidate.workExperience) {
        // Construct basic text from available fields
        const parts = [];
        if (hydratedCandidate.contactName) parts.push(`Name: ${hydratedCandidate.contactName}`);
        if (hydratedCandidate.job_title || hydratedCandidate.title) parts.push(`Title: ${hydratedCandidate.job_title || hydratedCandidate.title}`);
        if (hydratedCandidate.email) parts.push(`Email: ${hydratedCandidate.email}`);
        if (Array.isArray(hydratedCandidate.workExperience) && hydratedCandidate.workExperience.length > 0) {
          parts.push('\nWork Experience:\n' + JSON.stringify(hydratedCandidate.workExperience, null, 2));
        }
        fullText = parts.join('\n');
      }

      // Get evaluation data if available for this candidate-job pair
      let evaluationData = null;
      if (candidate.evaluations && candidate.evaluations[selectedJob]) {
        evaluationData = candidate.evaluations[selectedJob];
      }

      const payload = {
        contactId: hydratedCandidate.id || candidate.id,
        contactName: hydratedCandidate.contactName || candidate.contactName,
        title: hydratedCandidate.job_title || hydratedCandidate.title || candidate.job_title || candidate.title || '',
        fullText: fullText,
        lastEvaluatedJob: selectedJobData ? {
          id: selectedJob,
          title: selectedJobData.title || '',
          company: selectedJobData.company || ''
        } : null,
        evaluationSummary: evaluationData?.rating?.explanation || null,
        brand: { bannerUrl: FIREBASE_BANNER_URL },
        format: 'html-pdf',  // Use HTML-to-PDF format with Playwright
        anonymize: anonymizeName,  // Include anonymization setting
        skillsOverride: Array.from(new Set([
          ...normalizeSkillList(hydratedCandidate.ai_skills),
          ...normalizeSkillList(hydratedCandidate.skills),
          ...normalizeSkillList(hydratedCandidate.Skills),
          ...normalizeSkillList(hydratedCandidate.tags?.skills),
          ...normalizeSkillList(hydratedCandidate.matchingContext?.key_matches)
        ])).slice(0, 20)
      };

      const resp = await contactsApi.formatCV(payload);
      if (resp?.success && resp.pdfB64) {
        setSelectedCvPdfB64(resp.pdfB64);
        // Optionally download for preview
        const link = document.createElement('a');
        const filename = anonymizeName
          ? `Confidential-Candidate-branded.pdf`
          : `${candidate.contactName || 'cv'}-branded.pdf`;
        link.href = `data:application/pdf;base64,${resp.pdfB64}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('Branded CV generated successfully! It will be attached when you send the email.');
      } else {
        message.error(resp?.error || 'Failed to generate branded CV');
      }
    } catch (error) {
      console.error('Error generating branded CV:', error);
      message.error('Failed to generate branded CV');
    } finally {
      setFormatting(false);
    }
  };

  // Generate JD companion PDF for email attachment.
  const handleGenerateJdPdf = async () => {
    if (!selectedJob || !selectedJobData) {
      message.warning('Please select a job first');
      return;
    }

    try {
      setJdFormatting(true);
      const token = localStorage.getItem('token');
      const jobTitle = selectedJobData?.title || 'Role';
      const jobDescription = selectedJobData?.description || selectedJobData?.opportunityBrief || '';

      if (!jobDescription.trim()) {
        message.warning('Selected job has no description to build a JD PDF.');
        return;
      }

      // Ensure a QR code exists for this job so the JD companion includes
      // an ALICE touchpoint for interested candidates.
      const qrResp = await axios.post(`/api/qr/jobs/${encodeURIComponent(selectedJob)}/ensure`, {
        variant: 'email_jd_pdf',
        includeImage: true
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!qrResp?.data?.success || !qrResp?.data?.qr?.qr_url) {
        message.error(qrResp?.data?.error || 'Failed to prepare QR code for JD PDF.');
        return;
      }

      const qrUrl = qrResp?.data?.qr?.qr_url;
      const qrImageUrl = qrResp?.data?.qr?.qr_image_data_url || null;

      const response = await axios.post('/api/email/debug-jd-companion', {
        jobTitle,
        jobDescription,
        includeQr: true,
        qrUrl,
        qrImageUrl,
        includePdfB64: true,
        includeHtml: false,
        persistAudit: true
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response?.data?.success && response?.data?.pdfB64) {
        setSelectedJdPdfB64(response.data.pdfB64);
        message.success('JD PDF with QR generated. It will be attached when you send the email.');
      } else {
        message.error(response?.data?.error || 'Failed to generate JD PDF');
      }
    } catch (error) {
      console.error('Error generating JD PDF:', error);
      message.error(error?.response?.data?.error || 'Failed to generate JD PDF');
    } finally {
      setJdFormatting(false);
    }
  };

  const openCandidateProfileBuilder = () => {
    if (!candidate) {
      message.warning('Please select a candidate first');
      return;
    }

    navigate('/candidate-profile-builder', {
      state: {
        backCandidateId: candidate?.id || candidate?.candidate_id || '',
        cvData: {
          ...candidate,
          contactName: candidate?.contactName || candidate?.name || candidate?.fullName || '',
          title: candidate?.job_title || candidate?.title || '',
          text: candidate?.fullText || candidate?.full_text || candidate?.text || '',
          workExperience: candidate?.workExperience || candidate?.work_experience || candidate?.professionalExperience || [],
          work_experience: candidate?.work_experience || candidate?.workExperience || [],
          professionalExperience: candidate?.professionalExperience || candidate?.workExperience || [],
          experience: candidate?.experience || candidate?.workExperience || [],
          portraitUrl: candidate?.portraitUrl || candidate?.photoUrl || candidate?.photo_url || '',
          portraitAlt: candidate?.portraitAlt || candidate?.contactName || candidate?.name || '',
          education: candidate?.education || candidate?.educationHistory || [],
          educationHistory: candidate?.educationHistory || candidate?.education || [],
          languages: candidate?.languages || candidate?.languageSkills || [],
          systems: candidate?.systems || candidate?.tools || [],
          yearsOfExperience: candidate?.yearsOfExperience ?? candidate?.years_experience ?? undefined,
          skills: candidate?.skills || candidate?.ai_skills || [],
          ai_skills: candidate?.ai_skills || candidate?.skills || [],
          evaluationSummary: candidate?.evaluationSummary || null,
          lastEvaluatedJob: candidate?.lastEvaluatedJob || null,
          confidentialityText: candidate?.confidentialityText || ''
        }
      }
    });
  };

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
      fetchEmailTemplates();
      // Reset CV state when modal opens
      setSelectedCvPdfB64(null);
      setSelectedJdPdfB64(null);
      setSelectedJobData(null);
      setSelectedJob(null);
      setSelectedTemplate(null);
      setAnonymizeName(false);  // Reset anonymization checkbox
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && candidate) {
      form.setFieldsValue({
        to_email: candidate.email || '',
        subject: `Exciting career opportunity for ${candidate.contactName}`,
        message: `Hi ${candidate.contactName},\n\nI hope this message finds you well. I came across your profile and was impressed by your background in ${candidate.job_title || candidate.title || 'your field'}.\n\nI would love to discuss an exciting opportunity that I believe would be a great fit for your skills and experience.\n\nBest regards,\n[Your Name]`,
      });
    }
  }, [isOpen, candidate, form]);

  // Apply email template
  const applyTemplate = async (templateId) => {
    if (!selectedJob || !selectedJobData) {
      message.warning('Please select a job first');
      return;
    }

    try {
      const template = emailTemplates.find(t => t.id === templateId);
      if (!template) {
        message.error('Template not found');
        return;
      }

      // Prepare variables for template rendering
      const variables = {
        candidate_name: candidate.contactName || '',
        candidate_title: candidate.job_title || candidate.title || '',
        job_title: selectedJobData.title || '',
        company_name: selectedJobData.company || '',
        job_description: selectedJobData.description || selectedJobData.opportunityBrief || ''
      };

      // Render template by replacing placeholders
      let subject = template.subject || '';
      let body = template.body || '';

      // Replace placeholders {{variable_name}}
      Object.keys(variables).forEach(key => {
        const placeholder = `{{${key}}}`;
        const value = variables[key];
        subject = subject.replace(new RegExp(placeholder, 'g'), value);
        body = body.replace(new RegExp(placeholder, 'g'), value);
      });

      // Set form values
      form.setFieldsValue({
        subject: subject,
        message: body
      });

      setSelectedTemplate(templateId);
      message.success('Template applied successfully');
    } catch (error) {
      console.error('Error applying template:', error);
      message.error('Failed to apply template');
    }
  };

  // Generate email content using AI
  const generateEmail = async () => {
    if (!selectedJob) {
      message.warning('Please select a job first');
      return;
    }

    try {
      setGenLoading(true);
      setSelectedTemplate(null); // Clear template selection when generating AI email
      const response = await axios.post('/api/candidates/generate-email', {
        candidate_id: candidate.id,
        job_id: selectedJob,
        candidate_name: candidate.contactName,
        candidate_title: candidate.job_title || candidate.title,
        candidate_skills: candidate.skills || [],
        candidate_experience: candidate.workExperience || []
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        form.setFieldsValue({
          subject: response.data.subject,
          message: response.data.message
        });
        message.success('Email generated successfully');
      } else {
        message.error(response.data.error || 'Failed to generate email');
      }
    } catch (error) {
      console.error('Error generating email:', error);
      message.error('Failed to generate email');
    } finally {
      setGenLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!candidate) return;

    try {
      setLoading(true);

      const payload = {
        candidate_id: candidate.id,
        to_email: values.to_email,
        subject: values.subject,
        message: values.message,
        job_id: selectedJob,
        candidate_name: candidate.contactName,
        candidate_title: candidate.job_title || candidate.title
      };

      // Endpoint supports a single attachment field.
      // Prefer JD PDF when available, otherwise attach branded CV.
      if (selectedJdPdfB64) {
        const cleanJobName = (selectedJobData?.title || 'Job')
          .replace(/^job_descriptions[\\/]/i, '')
          .replace(/\.(docx?|pdf)$/i, '')
          .replace(/[_-]+/g, ' ')
          .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        payload.attachmentPdfB64 = selectedJdPdfB64;
        payload.attachmentName = `JD - ${cleanJobName} Opportunity.pdf`;
      } else if (selectedCvPdfB64) {
        payload.attachmentPdfB64 = selectedCvPdfB64;
        payload.attachmentName = `${candidate.contactName || 'candidate'}-branded-resume.pdf`;
      }

      const response = await axios.post('/api/candidates/send-email', payload, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const attachmentMsg = selectedJdPdfB64
          ? ' with JD PDF attachment'
          : (selectedCvPdfB64 ? ' with branded CV attachment' : '');
        message.success(`Email sent successfully${attachmentMsg}!`);
        onClose && onClose(true); // Pass true to indicate success for potential refresh
        form.resetFields();
        setSelectedCvPdfB64(null); // Clear CV state
        setSelectedJdPdfB64(null); // Clear JD state
      } else {
        message.error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      message.error('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MailOutlined />
          <span>Send Email to {candidate?.contactName || 'Candidate'}</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          to_email: candidate?.email || '',
          subject: '',
          message: ''
        }}
      >
        {/* Candidate Info */}
        <div style={{
          marginBottom: 16,
          padding: 12,
          backgroundColor: '#f5f5f5',
          borderRadius: 6,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {candidate?.contactName}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {candidate?.job_title || candidate?.title || 'No title'} | {
              (() => {
                const location = candidate?.location;
                if (!location) return 'No location';

                // Handle location as object with city/country
                if (typeof location === 'object') {
                  const city = location.city || '';
                  const country = location.country || '';
                  return city && country ? `${city}, ${country}` : city || country || 'No location';
                }

                // Handle location as string
                return location;
              })()
            }
          </div>
        </div>

                 {/* Job Selection */}
         <Form.Item
           label="Select Job"
           name="job_id"
           rules={[{ required: true, message: 'Please select a job' }]}
         >
           <Select
             placeholder={jobs.length > 0 ? "Select a job to send email about" : "No jobs available"}
             onChange={handleJobSelect}
             showSearch
             disabled={jobs.length === 0}
             filterOption={safeSelectFilter}
             optionFilterProp="label"
           >
             {jobs.length > 0 ? (
               jobs.map(job => (
                 <Option key={job.id} value={job.id} label={`${job.title} - ${job.company}`}>
                   {job.title} - {job.company}
                 </Option>
               ))
             ) : (
               <Option value="" disabled>
                 No jobs available. Please create a job first.
               </Option>
             )}
           </Select>
         </Form.Item>

                 {/* Email Template Selection */}
         <Form.Item label="Use Email Template (Optional)">
           {emailTemplates.length > 0 ? (
             <Select
               placeholder="Select an email template"
               value={selectedTemplate}
               onChange={applyTemplate}
               loading={loadingTemplates}
               allowClear
               showSearch
               filterOption={safeSelectFilter}
               optionFilterProp="label"
               disabled={!selectedJob || jobs.length === 0}
             >
               {emailTemplates.map(template => (
                 <Option
                   key={template.id}
                   value={template.id}
                   label={`${template.name}${template.description ? ` - ${template.description}` : ''}`}
                 >
                   {template.name} {template.description ? `- ${template.description}` : ''}
                 </Option>
               ))}
             </Select>
           ) : (
             <div style={{
               padding: '8px 12px',
               backgroundColor: '#f5f5f5',
               borderRadius: '4px',
               fontSize: '12px',
               color: '#666',
               border: '1px dashed #d9d9d9'
             }}>
               {loadingTemplates ? (
                 'Loading templates...'
               ) : (
                 <>
                   No email templates found. Templates can be created via the Settings page or API.
                   <br />
                   <span style={{ fontSize: '11px', color: '#999' }}>
                     Use placeholders like {'{{candidate_name}}'}, {'{{job_title}}'}, {'{{company_name}}'} in templates.
                   </span>
                 </>
               )}
             </div>
           )}
         </Form.Item>

                 {/* Action Buttons */}
         <div style={{ marginBottom: 16, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
           <Button
             type="dashed"
             icon={<ReloadOutlined />}
             loading={genLoading}
             onClick={generateEmail}
             disabled={!selectedJob || jobs.length === 0}
           >
             Generate AI Email
           </Button>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Checkbox
              checked={anonymizeName}
              onChange={(e) => setAnonymizeName(e.target.checked)}
              disabled={!selectedJob || jobs.length === 0}
            >
              Anonymize candidate name
            </Checkbox>
            <Button
              type="default"
              icon={<FileTextOutlined />}
              onClick={openCandidateProfileBuilder}
              disabled={!candidate}
            >
              Open Resume Builder
            </Button>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              loading={formatting}
               onClick={handlePreviewBrandedCV}
               disabled={!selectedJob || jobs.length === 0}
              >
               Preview Branded CV
             </Button>
             <Button
               type="default"
               icon={<FileTextOutlined />}
               loading={jdFormatting}
               onClick={handleGenerateJdPdf}
               disabled={!selectedJob || jobs.length === 0}
             >
               Generate JD PDF
             </Button>
           </Space>
           {selectedCvPdfB64 && !selectedJdPdfB64 && (
             <Tag color="green" style={{ alignSelf: 'center' }}>
               CV will be attached
             </Tag>
           )}
           {selectedJdPdfB64 && (
             <Tag color="blue" style={{ alignSelf: 'center' }}>
               JD PDF (with QR) will be attached
             </Tag>
           )}
           {jobs.length === 0 && (
             <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px', width: '100%' }}>
               No jobs available. Please create a job first to generate emails.
             </div>
           )}
         </div>

        {/* Email Fields */}
        <Form.Item
          label="To Email"
          name="to_email"
          rules={[
            { required: true, message: 'Please enter email address' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="candidate@email.com" />
        </Form.Item>

        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, message: 'Please enter email subject' }]}
        >
          <Input placeholder="Exciting career opportunity" />
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Message</span>
              <span style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'normal' }}>
                Supports markdown: **bold**, *italic*, - bullets, [links](url)
              </span>
            </div>
          }
          name="message"
          rules={[{ required: true, message: 'Please enter email message' }]}
        >
          <TextArea
            rows={8}
            placeholder="Write your email message here...&#10;&#10;You can use markdown formatting:&#10;**bold text** for bold&#10;*italic text* for italic&#10;- Bullet point&#10;1. Numbered list&#10;[Link text](https://example.com) for links"
          />
        </Form.Item>

        {/* Action Buttons */}
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<MailOutlined />}
            >
              Send Email
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default SendCandidateEmailModal;

